import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Download, 
  Star,
  Users,
  Clock,
  FileText,
  Zap,
  Home,
  ShoppingCart,
  DollarSign
} from 'lucide-react';

export const EbooksPage = () => {
  const { t, i18n } = useTranslation();
  const [cart, setCart] = useState([]);

  const ebooks = [
    {
      id: 'energy-savings',
      title: i18n.language === 'es' ? 'Guía Completa de Ahorro Energético' : 'Guide Complet de l\'Économie d\'Énergie',
      description: i18n.language === 'es' 
        ? 'Descubre 50+ técnicas probadas para reducir tus facturas de electricidad y gas hasta un 40%'
        : 'Découvrez 50+ techniques éprouvées pour réduire vos factures d\'électricité et de gaz jusqu\'à 40%',
      price: 9.99,
      originalPrice: 19.99,
      pages: 120,
      rating: 4.8,
      reviews: 1247,
      category: i18n.language === 'es' ? 'Energía' : 'Énergie',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      features: i18n.language === 'es' ? [
        'Análisis de consumo paso a paso',
        'Negociación con proveedores',
        'Equipos de bajo consumo',
        'Energías renovables domésticas',
        'Trucos estacionales',
        'Calculadoras incluidas'
      ] : [
        'Analyse de consommation étape par étape',
        'Négociation avec fournisseurs',
        'Équipements basse consommation',
        'Énergies renouvelables domestiques',
        'Astuces saisonnières',
        'Calculatrices incluses'
      ],
      preview: i18n.language === 'es' 
        ? 'En este libro aprenderás a identificar los principales gastos energéticos de tu hogar y aplicar técnicas específicas para cada área. Incluye casos reales y tablas de comparación.'
        : 'Dans ce livre, vous apprendrez à identifier les principales dépenses énergétiques de votre foyer et à appliquer des techniques spécifiques pour chaque zone. Inclut des cas réels et des tableaux de comparaison.'
    },
    {
      id: 'grocery-smart',
      title: i18n.language === 'es' ? 'Compras Inteligentes y Presupuesto Familiar' : 'Courses Intelligentes et Budget Familial',
      description: i18n.language === 'es'
        ? 'Estrategias avanzadas para reducir gastos alimentarios sin sacrificar calidad ni variedad'
        : 'Stratégies avancées pour réduire les dépenses alimentaires sans sacrifier qualité ni variété',
      price: 7.99,
      originalPrice: 15.99,
      pages: 95,
      rating: 4.6,
      reviews: 892,
      category: i18n.language === 'es' ? 'Hogar' : 'Foyer',
      icon: Home,
      color: 'from-green-500 to-emerald-500',
      features: i18n.language === 'es' ? [
        'Planificación de menús económicos',
        'Mejores apps y sitios de descuentos',
        'Técnicas de batch cooking',
        'Comparativa supermercados',
        'Cultivo urbano básico',
        'Plantillas de presupuesto'
      ] : [
        'Planification de menus économiques',
        'Meilleures apps et sites de réductions',
        'Techniques de batch cooking',
        'Comparatif supermarchés',
        'Culture urbaine de base',
        'Templates de budget'
      ],
      preview: i18n.language === 'es'
        ? 'Guía práctica con métodos probados para optimizar tus compras. Incluye listas de verificación, apps recomendadas y estrategias de planificación familiar.'
        : 'Guide pratique avec méthodes éprouvées pour optimiser vos courses. Inclut check-lists, apps recommandées et stratégies de planification familiale.'
    },
    {
      id: 'subscriptions-audit',
      title: i18n.language === 'es' ? 'Auditoría de Suscripciones y Servicios' : 'Audit des Abonnements et Services',
      description: i18n.language === 'es'
        ? 'Identifica y elimina gastos ocultos en suscripciones, seguros y servicios no esenciales'
        : 'Identifiez et éliminez les dépenses cachées en abonnements, assurances et services non-essentiels',
      price: 6.99,
      originalPrice: 12.99,
      pages: 75,
      rating: 4.7,
      reviews: 634,
      category: i18n.language === 'es' ? 'Finanzas' : 'Finances',
      icon: FileText,
      color: 'from-blue-500 to-indigo-500',
      features: i18n.language === 'es' ? [
        'Checklist de 200+ servicios',
        'Técnicas de cancelación',
        'Negociación de tarifas',
        'Alternativas gratuitas',
        'Calendario de revisiones',
        'Hojas de cálculo incluidas'
      ] : [
        'Checklist de 200+ services',
        'Techniques d\'annulation',
        'Négociation de tarifs',
        'Alternatives gratuites',
        'Calendrier de révisions',
        'Feuilles de calcul incluses'
      ],
      preview: i18n.language === 'es'
        ? 'Metodología completa para revisar todos tus servicios contratados. Incluye scripts de llamada y plantillas de emails para negociar mejores condiciones.'
        : 'Méthodologie complète pour réviser tous vos services contractés. Inclut scripts d\'appel et templates d\'emails pour négocier de meilleures conditions.'
    },
    {
      id: 'moving-guide',
      title: i18n.language === 'es' ? 'Mudanza Económica: Guía Paso a Paso' : 'Déménagement Économique : Guide Pas-à-Pas',
      description: i18n.language === 'es'
        ? 'Organiza tu mudanza de forma eficiente y económica con esta guía completa'
        : 'Organisez votre déménagement de façon efficace et économique avec ce guide complet',
      price: 8.99,
      originalPrice: 16.99,
      pages: 85,
      rating: 4.9,
      reviews: 456,
      category: i18n.language === 'es' ? 'Hogar' : 'Foyer',
      icon: Home,
      color: 'from-purple-500 to-pink-500',
      features: i18n.language === 'es' ? [
        'Timeline completa 8 semanas',
        'Presupuestos y comparativas',
        'Trámites administrativos',
        'Tips de embalaje profesional',
        'Cambios de servicios',
        'Plantillas de documentos'
      ] : [
        'Timeline complète 8 semaines',
        'Budgets et comparatifs',
        'Démarches administratives',
        'Tips d\'emballage professionnel',
        'Changements de services',
        'Templates de documents'
      ],
      preview: i18n.language === 'es'
        ? 'Guide structurado con timeline detallado para cada etapa. Incluye calculadoras de costos, listas de verificación y contactos útiles.'
        : 'Guide structuré avec timeline détaillé pour chaque étape. Inclut calculateurs de coûts, check-lists et contacts utiles.'
    },
    {
      id: 'insurance-optimization',
      title: i18n.language === 'es' ? 'Optimización de Seguros y Protecciones' : 'Optimisation Assurances et Protections',
      description: i18n.language === 'es'
        ? 'Encuentra la cobertura ideal al mejor precio para auto, hogar, salud y vida'
        : 'Trouvez la couverture idéale au meilleur prix pour auto, habitation, santé et vie',
      price: 11.99,
      originalPrice: 22.99,
      pages: 140,
      rating: 4.5,
      reviews: 721,
      category: i18n.language === 'es' ? 'Seguros' : 'Assurances',
      icon: FileText,
      color: 'from-red-500 to-rose-500',
      features: i18n.language === 'es' ? [
        'Análisis de necesidades reales',
        'Comparadores especializados',
        'Técnicas de negociación',
        'Coberturas innecesarias comunes',
        'Calendario de revisiones',
        'Modelos de cartas incluidos'
      ] : [
        'Analyse des besoins réels',
        'Comparateurs spécialisés',
        'Techniques de négociation',
        'Couvertures inutiles courantes',
        'Calendrier de révisions',
        'Modèles de lettres inclus'
      ],
      preview: i18n.language === 'es'
        ? 'Méthodologie para evaluar y optimizar todas tus pólizas de seguros. Incluye comparativas de mercado y estrategias de negociación probadas.'
        : 'Méthodologie pour évaluer et optimiser toutes vos polices d\'assurance. Inclut comparatifs marché et stratégies de négociation éprouvées.'
    }
  ];

  const addToCart = (ebook) => {
    if (!cart.find(item => item.id === ebook.id)) {
      setCart([...cart, ebook]);
      toast.success(i18n.language === 'es' ? '¡Añadido al carrito!' : 'Ajouté au panier !');
    } else {
      toast.info(i18n.language === 'es' ? 'Ya está en el carrito' : 'Déjà dans le panier');
    }
  };

  const removeFromCart = (ebookId) => {
    setCart(cart.filter(item => item.id !== ebookId));
    toast.success(i18n.language === 'es' ? 'Eliminado del carrito' : 'Retiré du panier');
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0).toFixed(2);
  };

  const getTotalSavings = () => {
    return cart.reduce((total, item) => total + (item.originalPrice - item.price), 0).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="ebooks-page-title"
            >
              {t('tools.ebooks.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto mb-6">
              {i18n.language === 'es'
                ? 'Colección de guías prácticas para optimizar tus gastos domésticos. Estrategias probadas con casos reales y herramientas incluidas.'
                : 'Collection de guides pratiques pour optimiser vos dépenses domestiques. Stratégies éprouvées avec cas réels et outils inclus.'
              }
            </p>
            
            <div className="max-w-lg mx-auto p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 mb-8">
              <div className="flex items-center gap-2 justify-center text-amber-800">
                <Clock className="w-5 h-5" />
                <span className="font-medium">
                  {i18n.language === 'es' ? 'Próximamente Disponible' : 'Bientôt Disponible'}
                </span>
              </div>
              <p className="text-sm text-amber-700 mt-2 text-center">
                {i18n.language === 'es' 
                  ? 'Los e-books estarán disponibles para compra en las próximas semanas.'
                  : 'Les e-books seront disponibles à l\'achat dans les prochaines semaines.'
                }
              </p>
              
              {/* Encart Guide Anti-Inflation - SEULEMENT FRANCE */}
              {i18n.language !== 'es' && (
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-4 mt-4 text-white text-center">
                  <h4 className="font-semibold mb-2">📖 En attendant :</h4>
                  <p className="text-blue-100 text-sm mb-3">
                    Consultez notre <strong>Guide Anti-Inflation 2026</strong> gratuit avec des conseils pratiques pour économiser dès maintenant !
                  </p>
                  <Button 
                    asChild
                    size="sm"
                    className="bg-white text-blue-600 hover:bg-blue-50"
                  >
                    <Link to="/fr/guide-anti-inflation">
                      Consulter le Guide Gratuit
                    </Link>
                  </Button>
                </div>
              )}
            </div>

            {/* Cart Summary */}
            {cart.length > 0 && (
              <div className="max-w-md mx-auto mb-8">
                <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium text-emerald-800">
                          {cart.length} {i18n.language === 'es' ? 'libro(s)' : 'livre(s)'}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-emerald-800">{getTotalPrice()}€</div>
                        <div className="text-xs text-emerald-600">
                          {i18n.language === 'es' ? 'Ahorro:' : 'Économie:'} {getTotalSavings()}€
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* E-books Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ebooks.map((ebook) => {
              const isInCart = cart.find(item => item.id === ebook.id);
              const IconComponent = ebook.icon;

              return (
                <Card 
                  key={ebook.id}
                  className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 shadow-lg overflow-hidden bg-white relative"
                  data-testid={`ebook-card-${ebook.id}`}
                >
                  {/* Discount Badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-red-500 text-white">
                      -{Math.round(((ebook.originalPrice - ebook.price) / ebook.originalPrice) * 100)}%
                    </Badge>
                  </div>

                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${ebook.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {ebook.category}
                      </Badge>
                    </div>
                    
                    <CardTitle className="text-xl font-bold text-sage-900 leading-tight mb-2">
                      {ebook.title}
                    </CardTitle>

                    {/* Rating and Stats */}
                    <div className="flex items-center gap-4 text-sm text-sage-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">{ebook.rating}</span>
                        <span>({ebook.reviews})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{ebook.pages}p</span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <CardDescription className="text-sage-600 mb-4 leading-relaxed line-clamp-3">
                      {ebook.description}
                    </CardDescription>

                    {/* Pricing */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-2xl font-bold text-sage-900">
                        {ebook.price}€
                      </div>
                      <div className="text-lg text-gray-400 line-through">
                        {ebook.originalPrice}€
                      </div>
                    </div>

                    <Separator className="mb-4" />

                    {/* Actions */}
                    <div className="space-y-2">
                      <Button 
                        className="w-full bg-gradient-to-r from-gray-400 to-gray-500 text-white cursor-not-allowed"
                        disabled
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        {i18n.language === 'es' ? 'Próximamente disponible' : 'Bientôt disponible'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Checkout Section */}
      {cart.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-sage-600 to-sage-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              {i18n.language === 'es' ? '¡Completa tu compra!' : 'Finalisez votre achat !'}
            </h2>
            <p className="text-sage-100 text-lg mb-8">
              {i18n.language === 'es' 
                ? `${cart.length} libro(s) seleccionado(s) - Ahorro total: ${getTotalSavings()}€`
                : `${cart.length} livre(s) sélectionné(s) - Économie totale : ${getTotalSavings()}€`
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="text-white">
                <div className="text-3xl font-bold">{getTotalPrice()}€</div>
                <div className="text-sm text-sage-200">
                  {i18n.language === 'es' ? 'Total a pagar' : 'Total à payer'}
                </div>
              </div>
              
              <Button 
                size="lg"
                className="bg-white text-sage-700 hover:bg-sage-50 px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() => toast.info(i18n.language === 'es' ? 'Funcionalidad de pago próximamente' : 'Fonctionnalité de paiement prochainement')}
              >
                <DollarSign className="w-5 h-5 mr-2" />
                {i18n.language === 'es' ? 'Proceder al pago' : 'Procéder au paiement'}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};